/**
 * Turning evaluations into report averages.
 *
 * The rules come from the spreadsheet this replaces
 * (`docs/source/Bulletins v5-2 (4).xlsx`). Its per-period formula reads
 *
 *   (SUM(marks) / SUMIF(marks, "<>A", maxima)) * 25
 *
 * so an "A" leaves the numerator *and* takes its maximum out of the
 * denominator: an absent pupil is not marked down, the evaluation simply never
 * happened for them. A blank cell is a zero, and its maximum still counts —
 * that is a pupil who sat the test and scored nothing.
 *
 * And its subject total,
 *
 *   100 * SUM(domain averages) / SUM(maxima of the domains that have one)
 *
 * which, every domain being out of 25, is just `100 × the mean of the domain
 * ratios`. Everything here therefore works in ratios between 0 and 1 and only
 * multiplies by a scale at the very end. A subject with no domains is then the
 * one-domain case, with no special path through the code.
 *
 * The one rule that is *not* in the spreadsheet: an average needs at least two
 * marks, otherwise it reads "NC". A single test does not make an average.
 */
import type { Evaluation } from '../store/types.ts';

/** Below this many real marks, a pupil's average is not computable. */
export const MIN_MARKS_FOR_AVERAGE = 2;

/** The three report periods of the school year, as stored in `period`. */
export const PERIODS = ['1', '2', '3'] as const;

export const PERIOD_LABEL: Readonly<Record<string, string>> = {
  '1': '1ère période',
  '2': '2e période',
  '3': '3e période',
};

/** The mark that means "absent", as typed by the teacher. */
export const ABSENT = 'A';

export type ParsedScore = { kind: 'value'; value: number } | { kind: 'absent' };

/**
 * Read one cell.
 *
 * Empty is a zero, `A` is an absence, and anything else unreadable is treated
 * as an absence too: excluding a mark nobody can interpret beats folding a
 * NaN into a school report.
 */
export function parseScore(raw: string): ParsedScore {
  const text = raw.trim();
  if (text === '') return { kind: 'value', value: 0 };
  if (text.toUpperCase() === ABSENT) return { kind: 'absent' };

  const n = Number(text.replace(',', '.'));
  return Number.isFinite(n) ? { kind: 'value', value: n } : { kind: 'absent' };
}

/** True for a cell the teacher may type: a number, a comma decimal, or "A". */
export function isValidScore(raw: string): boolean {
  const text = raw.trim();
  if (text === '' || text.toUpperCase() === ABSENT) return true;
  return Number.isFinite(Number(text.replace(',', '.')));
}

// ------------------------------------------------------------------ columns

/** One test: the rows for every pupil who sat it, under one heading. */
export interface EvaluationColumn {
  key: string;
  title: string;
  /** The `SUBJECTS` name, shown when a report block merges two subjects. */
  subject: string;
  domain: string;
  period: string;
  lessonId: string;
  /** Parsed maximum, 0 when unreadable. */
  max: number;
  /** The maximum as typed, so a row added later copies it verbatim. */
  maxRaw: string;
  /** ISO date of the earliest row; columns are ordered by it. */
  date: string;
  byStudent: ReadonlyMap<string, Evaluation>;
}

/**
 * Group rows into columns.
 *
 * Rows written by the same encoding pass share a lesson, so `lessonId` is the
 * natural key. Older rows, and anything typed outside a lesson, fall back to
 * everything that identifies the test.
 */
function columnKey(row: Evaluation): string {
  if (row.lessonId !== '') return `L:${row.lessonId}|${row.period}`;
  return `T:${row.subject}|${row.domain}|${row.period}|${row.title}|${row.max}`;
}

export function buildColumns(rows: readonly Evaluation[]): EvaluationColumn[] {
  const byKey = new Map<string, { column: EvaluationColumn; byStudent: Map<string, Evaluation> }>();

  for (const row of rows) {
    const key = columnKey(row);
    let entry = byKey.get(key);

    if (!entry) {
      const byStudent = new Map<string, Evaluation>();
      entry = {
        byStudent,
        column: {
          key,
          title: row.title,
          subject: row.subject,
          domain: row.domain,
          period: row.period,
          lessonId: row.lessonId,
          max: Number(row.max.replace(',', '.')) || 0,
          maxRaw: row.max,
          date: row.date,
          byStudent,
        },
      };
      byKey.set(key, entry);
    }

    // Rows of one column agree on everything but the mark; the earliest date
    // wins so a mark added late does not shuffle the column order.
    if (row.date !== '' && (entry.column.date === '' || row.date < entry.column.date)) {
      entry.column.date = row.date;
    }
    entry.byStudent.set(row.studentId, row);
  }

  return [...byKey.values()]
    .map((e) => e.column)
    .sort((a, b) => a.date.localeCompare(b.date) || a.title.localeCompare(b.title, 'fr'));
}

// ----------------------------------------------------------------- averages

export type Ratio = { kind: 'value'; value: number } | { kind: 'nc' };

export const NC: Ratio = { kind: 'nc' };

/**
 * A pupil's share of the points available across these columns, 0 to 1.
 *
 * A column the pupil has no row in is skipped exactly like an absence: they
 * were not in the class when it was marked, and charging them a zero for it
 * would be wrong.
 */
export function scoreRatio(studentId: string, columns: readonly EvaluationColumn[]): Ratio {
  let obtained = 0;
  let available = 0;
  let marks = 0;

  for (const column of columns) {
    const row = column.byStudent.get(studentId);
    if (!row) continue;

    const parsed = parseScore(row.score);
    if (parsed.kind === 'absent') continue;

    obtained += parsed.value;
    available += column.max;
    marks += 1;
  }

  if (marks < MIN_MARKS_FOR_AVERAGE || available <= 0) return NC;
  return { kind: 'value', value: obtained / available };
}

/** The whole block out of 100: the mean of the domains that have an average. */
export function bulletinTotal(ratios: readonly Ratio[]): Ratio {
  const values = ratios.filter((r): r is { kind: 'value'; value: number } => r.kind === 'value');
  if (values.length === 0) return NC;

  const sum = values.reduce((total, r) => total + r.value, 0);
  return { kind: 'value', value: sum / values.length };
}

/**
 * A pupil's whole report block, out of 100 once scaled.
 *
 * `domains` empty means the block has none, and its evaluations form a single
 * pool — which is the same computation with one term. `rows` must already be
 * narrowed to this block and this period.
 */
export function blockRatio(
  studentId: string,
  domains: readonly string[],
  rows: readonly Evaluation[],
): Ratio {
  if (domains.length === 0) return scoreRatio(studentId, buildColumns(rows));

  return bulletinTotal(
    domains.map((domain) =>
      scoreRatio(studentId, buildColumns(rows.filter((r) => r.domain === domain))),
    ),
  );
}

// ----------------------------------------------------------------- display

/** "18,4", or "NC" when there is nothing to show. */
export function formatRatio(ratio: Ratio, scale: number): string {
  if (ratio.kind === 'nc') return 'NC';
  return (ratio.value * scale).toLocaleString('fr-BE', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}
